import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupplierDetail, SupplierDuplicateMatch } from '@optiflow/types';
import { MAX_SUPPLIER_PAYMENT_TERMS_DAYS } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useCreateSupplierMutation,
  useSupplierDuplicateCheck,
  useUpdateSupplierMutation,
  type SupplierInput,
} from '../api/suppliers.api';

interface SupplierFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** Absent when registering a new supplier. */
  supplier?: SupplierDetail;
  onSaved: (supplier: SupplierDetail) => void;
}

const EMPTY: SupplierInput = { name: '' };

function valuesOf(supplier: SupplierDetail | undefined): SupplierInput {
  if (!supplier) return { ...EMPTY };
  return {
    name: supplier.name,
    nameAr: supplier.nameAr ?? '',
    contactPerson: supplier.contactPerson ?? '',
    phone: supplier.phone ?? '',
    email: supplier.email ?? '',
    taxNumber: supplier.taxNumber ?? '',
    address: supplier.address ?? '',
    paymentTermsDays: supplier.paymentTermsDays,
    notes: supplier.notes ?? '',
  };
}

/** Blank strings mean "not given" to the API, which clears the field rather than storing an empty one. */
function payloadOf(values: SupplierInput): SupplierInput {
  const trimmed = (value: string | undefined) => {
    const next = value?.trim();
    return next ? next : undefined;
  };

  return {
    name: values.name.trim(),
    nameAr: trimmed(values.nameAr),
    contactPerson: trimmed(values.contactPerson),
    phone: trimmed(values.phone),
    email: trimmed(values.email),
    taxNumber: trimmed(values.taxNumber),
    address: trimmed(values.address),
    paymentTermsDays: values.paymentTermsDays ?? 0,
    notes: trimmed(values.notes),
  };
}

/**
 * Registering a supplier, or saving one.
 *
 * The supplier number is never a field: the server allocates `SUP-000001` and the form would only
 * be guessing. Possible duplicates are shown as a warning the person reads and decides on — two
 * real companies can share a name, a phone or even an accountant's tax number, and refusing the
 * second one would be the form inventing a rule the business does not have.
 */
export function SupplierFormDialog({
  isOpen,
  onClose,
  supplier,
  onSaved,
}: SupplierFormDialogProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<SupplierInput>(() => valuesOf(supplier));
  const [matches, setMatches] = useState<SupplierDuplicateMatch[]>([]);

  const create = useCreateSupplierMutation();
  const update = useUpdateSupplierMutation();
  const duplicates = useSupplierDuplicateCheck();
  const isSaving = create.isPending || update.isPending;
  const error = apiErrorMessage(create.error) ?? apiErrorMessage(update.error);

  useEffect(() => {
    if (isOpen) {
      setValues(valuesOf(supplier));
      setMatches([]);
    }
  }, [isOpen, supplier]);

  const set = (field: keyof SupplierInput) => (value: string) =>
    setValues((current) => ({ ...current, [field]: value }));

  /** Asked as the person leaves the field, so a hint arrives while they can still act on it. */
  const checkDuplicates = () => {
    const payload = payloadOf(values);
    if (!payload.name && !payload.phone && !payload.taxNumber) return;

    duplicates.mutate(
      {
        name: payload.name,
        nameAr: payload.nameAr,
        phone: payload.phone,
        taxNumber: payload.taxNumber,
        ...(supplier ? { excludeSupplierId: supplier.id } : {}),
      },
      { onSuccess: (result) => setMatches(result.matches) },
    );
  };

  const submit = () => {
    const payload = payloadOf(values);
    if (!payload.name) return;

    if (supplier) {
      update.mutate(
        { id: supplier.id, version: supplier.version, ...payload },
        { onSuccess: onSaved },
      );
      return;
    }

    create.mutate(payload, { onSuccess: onSaved });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={supplier ? t('purchasing.suppliers.edit') : t('purchasing.suppliers.create')}
      description={
        supplier ? t('purchasing.suppliers.editHint') : t('purchasing.suppliers.createHint')
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={submit} isLoading={isSaving} disabled={!values.name.trim()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="error">{error}</Alert> : null}

        {matches.length > 0 ? (
          <Alert tone="warning">
            <p className="font-medium">{t('purchasing.suppliers.duplicates.title')}</p>
            <p className="text-sm">{t('purchasing.suppliers.duplicates.hint')}</p>
            <ul className="mt-2 space-y-1 text-sm">
              {matches.map((match) => (
                <li key={match.id}>
                  <span className="font-mono" dir="ltr">
                    {match.supplierNumber}
                  </span>{' '}
                  <span dir="auto">{match.name}</span>
                  {' — '}
                  {t(`purchasing.suppliers.duplicates.reason.${match.reason}`)}
                  {match.isActive ? '' : ` (${t('status.inactive')})`}
                </li>
              ))}
            </ul>
          </Alert>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t('purchasing.suppliers.fields.name')}
            value={values.name}
            required
            onChange={(event) => set('name')(event.target.value)}
            onBlur={checkDuplicates}
          />
          <Input
            label={t('purchasing.suppliers.fields.nameAr')}
            value={values.nameAr ?? ''}
            dir="auto"
            onChange={(event) => set('nameAr')(event.target.value)}
          />
          <Input
            label={t('purchasing.suppliers.fields.contactPerson')}
            value={values.contactPerson ?? ''}
            onChange={(event) => set('contactPerson')(event.target.value)}
          />
          <Input
            label={t('purchasing.suppliers.fields.phone')}
            value={values.phone ?? ''}
            dir="ltr"
            inputMode="tel"
            onChange={(event) => set('phone')(event.target.value)}
            onBlur={checkDuplicates}
          />
          <Input
            label={t('purchasing.suppliers.fields.email')}
            value={values.email ?? ''}
            dir="ltr"
            type="email"
            onChange={(event) => set('email')(event.target.value)}
          />
          <Input
            label={t('purchasing.suppliers.fields.taxNumber')}
            value={values.taxNumber ?? ''}
            dir="ltr"
            onChange={(event) => set('taxNumber')(event.target.value)}
            onBlur={checkDuplicates}
          />
          <Input
            label={t('purchasing.suppliers.fields.paymentTermsDays')}
            value={String(values.paymentTermsDays ?? 0)}
            dir="ltr"
            inputMode="numeric"
            hint={t('purchasing.suppliers.fields.paymentTermsHint')}
            onChange={(event) => {
              const next = Number(event.target.value.replace(/[^\d]/g, ''));
              setValues((current) => ({
                ...current,
                paymentTermsDays: Number.isFinite(next)
                  ? Math.min(next, MAX_SUPPLIER_PAYMENT_TERMS_DAYS)
                  : 0,
              }));
            }}
          />
          <Input
            label={t('purchasing.suppliers.fields.address')}
            value={values.address ?? ''}
            dir="auto"
            onChange={(event) => set('address')(event.target.value)}
          />
        </div>

        <label className="field-label" htmlFor="supplier-notes">
          {t('purchasing.suppliers.fields.notes')}
        </label>
        <textarea
          id="supplier-notes"
          className="border-line focus:border-brand-500 focus:ring-brand-100 w-full rounded-lg border bg-white px-3 py-2.5 text-sm focus:ring-4 focus:outline-none"
          rows={3}
          dir="auto"
          value={values.notes ?? ''}
          onChange={(event) => set('notes')(event.target.value)}
        />
      </div>
    </Modal>
  );
}
